import { useCallback, useEffect, useState } from 'react';
import { openWithToken, request } from '../../services/api';

const emptyTemplate = {
  title: '',
  category: '',
  description: '',
  template_body: '',
  template_fill_map: '',
  is_active: true,
};

export default function TemplatesPage({ token }) {
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [form, setForm] = useState(emptyTemplate);
  const [templatePdf, setTemplatePdf] = useState(null);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdownId, setActiveDropdownId] = useState(null);

  const load = useCallback(() => {
    request('/api/contracts/templates', { token }).then((response) => setTemplates(response.data));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (activeDropdownId && !event.target.closest('.dropdown-container')) {
        setActiveDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdownId]);

  const reset = () => {
    setForm(emptyTemplate);
    setTemplatePdf(null);
    setEditing(null);
    setView('list');
  };

  const startCreate = () => {
    setForm(emptyTemplate);
    setTemplatePdf(null);
    setEditing(null);
    setView('form');
  };

  const startEdit = (template) => {
    setForm(template);
    setEditing(template.id);
    setView('form');
    setActiveDropdownId(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    const path = editing ? `/api/contracts/templates/${editing}` : '/api/contracts/templates';
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => body.append(key, value ?? ''));
    if (templatePdf) body.append('template_pdf', templatePdf);
    await request(path, { token, method: editing ? 'PUT' : 'POST', body, form: true });
    setMessage(editing ? 'Template updated successfully.' : 'Template uploaded successfully.');
    reset();
    load();
    setTimeout(() => setMessage(''), 4000);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template permanently? This cannot be undone.')) return;
    await request(`/api/contracts/templates/${id}`, { token, method: 'DELETE' });
    setMessage('Template deleted.');
    load();
    setActiveDropdownId(null);
    setTimeout(() => setMessage(''), 4000);
  };

  const toggleStatus = async (template) => {
    await request(`/api/contracts/templates/${template.id}/toggle`, {
      token,
      method: 'PATCH',
      body: { is_active: !template.is_active },
    });
    setMessage(`Template status changed.`);
    load();
    setActiveDropdownId(null);
    setTimeout(() => setMessage(''), 4000);
  };

  const duplicate = async (id) => {
    await request(`/api/contracts/templates/${id}/duplicate`, { token, method: 'POST' });
    setMessage('Template duplicated successfully.');
    load();
    setActiveDropdownId(null);
    setTimeout(() => setMessage(''), 4000);
  };

  const filteredTemplates = templates.filter(t => 
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.category && t.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="stack">
      {message && <div className="alert success">{message}</div>}

      {view === 'list' ? (
        <section className="panel">
          <div className="panel-header-actions">
            <div>
              <h2>PDF Contract Library</h2>
              <p className="panel-subtitle">Manage contract templates and map input coordinates for automatic filling.</p>
            </div>
            <button className="primary" onClick={startCreate}>+ Create Template</button>
          </div>

          <div className="search-bar-container">
            <input 
              type="text" 
              placeholder="🔍 Search templates by title or category..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          {filteredTemplates.length === 0 ? (
            <div className="empty">No contract templates found. Click "+ Create Template" to add one.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Contract Title</th>
                    <th>Category</th>
                    <th>Template File</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map((template) => (
                    <tr key={template.id}>
                      <td>
                        <div className="template-info-cell">
                          <strong>{template.title}</strong>
                          {template.description && <span className="template-desc-sub">{template.description}</span>}
                        </div>
                      </td>
                      <td>
                        <span className="category-tag">{template.category || 'General'}</span>
                      </td>
                      <td>
                        <span className="file-name-text">{template.original_file_name || 'No PDF file'}</span>
                      </td>
                      <td>
                        <span className={`badge ${template.is_active ? 'signed' : 'rejected'}`}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-right actions-cell">
                        <div className="dropdown-container">
                          <button 
                            type="button" 
                            className="dropdown-toggle-btn"
                            onClick={() => setActiveDropdownId(activeDropdownId === template.id ? null : template.id)}
                          >
                            Actions ▾
                          </button>
                          {activeDropdownId === template.id && (
                            <div className="dropdown-menu-list">
                              <button type="button" onClick={() => startEdit(template)}>✏️ Edit</button>
                              {template.pdf_path && (
                                <button type="button" onClick={() => { openWithToken(`/api/contracts/templates/${template.id}/preview`, token); setActiveDropdownId(null); }}>
                                  👁️ Preview
                                </button>
                              )}
                              <button type="button" onClick={() => duplicate(template.id)}>📋 Duplicate</button>
                              <button type="button" onClick={() => toggleStatus(template)}>
                                {template.is_active ? '❌ Deactivate' : '✅ Activate'}
                              </button>
                              <button type="button" className="danger-text" onClick={() => deleteTemplate(template.id)}>🗑️ Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="panel single-panel-form">
          <div className="panel-header-actions">
            <div>
              <h2>{editing ? 'Edit PDF Contract' : 'Upload PDF Contract'}</h2>
              <p className="panel-subtitle">Configure settings and fields for client signature collection.</p>
            </div>
            <button type="button" className="ghost" onClick={reset}>← Back to Library</button>
          </div>

          <form onSubmit={submit} className="form-grid">
            <label className="full">Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Employment Contract, NDA, Service Agreement" /></label>
            <label>Category<input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. HR, Legal, Sales" /></label>
            <label>Description<input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief internal details about this template" /></label>
            
            <label className="full">PDF Contract File
              <input type="file" accept="application/pdf" onChange={(e) => setTemplatePdf(e.target.files[0])} required={!editing && !form.pdf_path} />
              {form.pdf_path && <span className="file-hint">Current File: {form.original_file_name || 'Template PDF'}</span>}
            </label>
            
            <label className="full">Note/Description for Signing Page<textarea rows="4" value={form.template_body || ''} onChange={(e) => setForm({ ...form, template_body: e.target.value })} placeholder="Instructions, welcome message, or legal terms displayed to signers on the public page..." /></label>
            
            <label className="full">PDF Fill Positions (JSON Coordinate Map)
              <textarea rows="6" value={form.template_fill_map || ''} onChange={(e) => setForm({ ...form, template_fill_map: e.target.value })} placeholder='{"recipient_full_name":{"page":1,"x":120,"y":650},"signature":{"page":1,"x":120,"y":120,"width":150,"height":55}}' />
              <span className="field-hint">Specify coordinates to draw the signer details and signatures directly into the template PDF. If omitted, a clean formal "Execution Page" will automatically be appended as the last page.</span>
            </label>
            
            <label className="check full"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            
            <div className="form-actions-row full">
              <button className="primary">{editing ? 'Save Changes' : 'Upload Contract Template'}</button>
              <button type="button" className="ghost" onClick={reset}>Cancel</button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
